import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getApiUrl } from '../../config/api';
import { useToast } from '../../context/ToastContext';
import styles from '../../styles/modules/admin/AdminUsers.module.css';
import adminCommonStyles from '../../styles/modules/admin/AdminCommon.module.css';
import headerStyles from '../../styles/modules/Header.module.css';

interface EmployerUser {
  _id: string;
  email: string;
  fullName: string;
  role: string;
  createdAt: string;
  jobPostsCount?: number;
  quickSearchCount?: number;
  subscription?: {
    jobPostsCount?: number;
    quickSearchCount?: number;
  };
}

type GiftTokenType = 'jobPost' | 'quickSearch';

type AmountChoice = '1' | '3' | '5' | 'custom';

interface GiftModalState {
  isOpen: boolean;
  user: EmployerUser | null;
  type: GiftTokenType;
  amountChoice: AmountChoice;
  customAmount: string;
}

const AdminTokens: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { showToast } = useToast();

  const [users, setUsers] = useState<EmployerUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [giftLoading, setGiftLoading] = useState<Record<string, { jobPost: boolean; quickSearch: boolean }>>({});
  const [giftModal, setGiftModal] = useState<GiftModalState>({
    isOpen: false,
    user: null,
    type: 'jobPost',
    amountChoice: '1',
    customAmount: '',
  });

  const fetchEmployers = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('authToken');
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: '20',
        ...(searchTerm && { search: searchTerm }),
        ...(roleFilter !== 'all' && { role: roleFilter }),
      });

      const response = await fetch(getApiUrl(`api/admin/employers?${params}`), {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const result = await response.json();
        setUsers(result.data || result.users || []);
        setTotalPages(result.pagination?.totalPages || result.pagination?.pages || 1);
      } else {
        showToast({ message: t('admin.tokens.fetchFailed', { defaultValue: 'Failed to load employer users.' }), type: 'error' });
      }
    } catch (error) {
      console.error('Error fetching employer users:', error);
      showToast({ message: t('admin.tokens.fetchFailed', { defaultValue: 'Failed to load employer users.' }), type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEmployers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, roleFilter, searchTerm]);

  const getRoleBadgeClass = (role: string) => {
    const normalizedRole = role.toLowerCase().replace(/\s+/g, '');
    const roleClassMap: Record<string, string> = {
      employer: adminCommonStyles.roleEmployer,
      admin: adminCommonStyles.roleAdmin,
      superadmin: adminCommonStyles.roleSuperadmin,
    };

    return [adminCommonStyles.roleBadge, roleClassMap[normalizedRole]]
      .filter(Boolean)
      .join(' ');
  };

  const getEmployerTokenCount = (user: EmployerUser, type: GiftTokenType) => {
    const directCount = type === 'jobPost' ? user.jobPostsCount : user.quickSearchCount;
    if (typeof directCount === 'number') {
      return directCount;
    }

    const nestedCount = type === 'jobPost'
      ? user.subscription?.jobPostsCount
      : user.subscription?.quickSearchCount;

    return typeof nestedCount === 'number' ? nestedCount : 'N/A';
  };

  const getTokenTypeLabel = (type: GiftTokenType) => {
    return type === 'jobPost'
      ? t('admin.tokens.giftJobPost', { defaultValue: 'Gift JobPost' })
      : t('admin.tokens.giftQuickSearch', { defaultValue: 'Gift QuickSearch' });
  };

  const openGiftModal = (user: EmployerUser, type: GiftTokenType) => {
    setGiftModal({
      isOpen: true,
      user,
      type,
      amountChoice: '1',
      customAmount: '',
    });
  };

  const closeGiftModal = () => {
    setGiftModal(prev => ({ ...prev, isOpen: false }));
  };

  const resolveGiftAmount = () => {
    const rawAmount = giftModal.amountChoice === 'custom' ? giftModal.customAmount : giftModal.amountChoice;
    return Number(rawAmount);
  };

  const getResolvedGiftAmountLabel = () => {
    if (giftModal.amountChoice !== 'custom') {
      return giftModal.amountChoice;
    }

    const parsed = Number(giftModal.customAmount);
    return Number.isInteger(parsed) && parsed > 0 ? String(parsed) : '?';
  };

  const handleGiftTokens = async (userId: string, type: GiftTokenType, amount: number) => {
    if (!Number.isInteger(amount) || amount <= 0) {
      showToast({
        message: t('admin.tokens.invalidAmount', { defaultValue: 'Amount must be a positive whole number.' }),
        type: 'error',
      });
      return false;
    }

    const endpoint = type === 'jobPost'
      ? `api/admin/subscriptions/${userId}/grant-job-post-tokens`
      : `api/admin/subscriptions/${userId}/grant-quick-search-tokens`;

    try {
      setGiftLoading(prev => ({
        ...prev,
        [userId]: {
          jobPost: prev[userId]?.jobPost || false,
          quickSearch: prev[userId]?.quickSearch || false,
          [type]: true,
        },
      }));

      const token = localStorage.getItem('authToken');
      const response = await fetch(getApiUrl(endpoint), {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ amount }),
      });

      if (response.ok) {
        const result = await response.json();
        showToast({
          message: result?.message || t('admin.tokens.giftSuccess', { defaultValue: 'Tokens granted successfully.' }),
          type: 'success',
        });

        fetchEmployers();
        return true;
      } else {
        const errorData = await response.json();
        showToast({
          message: errorData?.message || t('admin.tokens.giftFailed', { defaultValue: 'Failed to grant tokens.' }),
          type: 'error',
        });
        return false;
      }
    } catch (error) {
      console.error('Error gifting tokens:', error);
      showToast({
        message: t('admin.tokens.giftFailed', { defaultValue: 'Failed to grant tokens.' }),
        type: 'error',
      });
      return false;
    } finally {
      setGiftLoading(prev => ({
        ...prev,
        [userId]: {
          jobPost: type === 'jobPost' ? false : (prev[userId]?.jobPost || false),
          quickSearch: type === 'quickSearch' ? false : (prev[userId]?.quickSearch || false),
        },
      }));
    }
  };

  const confirmGiftFromModal = async () => {
    if (!giftModal.user) {
      return;
    }

    const amount = resolveGiftAmount();
    const success = await handleGiftTokens(giftModal.user._id, giftModal.type, amount);
    if (success) {
      closeGiftModal();
    }
  };

  return (
    <div className={styles.adminUsers}>
      <div className={styles.listPageHeader}>
        <button
          onClick={() => navigate('/admin')}
          className={headerStyles.backButton}
          aria-label={t('common.back')}
          title={t('common.back')}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M19 12H5M12 19l-7-7 7-7"/>
          </svg>
        </button>
        <h1 className={styles.headerTitle}>{t('admin.tokens.title', { defaultValue: 'Employer Token Management' })}</h1>
        <div></div>
      </div>

      <div className={styles.pageContent}>
        <div className={styles.userSectionCard}>
          <div className={styles.sectionHeader}>
            <h2>{t('admin.tokens.employers', { defaultValue: 'Employer Users' })}</h2>
            <span className={styles.userCount}>{users.length} {users.length === 1 ? 'user' : 'users'}</span>
          </div>

          <div className={styles.controlsSection}>
            <div className={styles.searchBox}>
              <input
                type="text"
                placeholder={t('admin.tokens.search', { defaultValue: 'Search employers...' })}
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
                className={styles.searchInput}
              />
            </div>

            <div className={styles.filters}>
              <select
                value={roleFilter}
                onChange={(e) => {
                  setRoleFilter(e.target.value);
                  setCurrentPage(1);
                }}
                className={styles.filterSelect}
                aria-label={t('admin.tokens.roleFilter', { defaultValue: 'Filter by role' })}
              >
                <option value="all">All Roles</option>
                <option value="venue_owner">venue_owner</option>
                <option value="manager">manager</option>
              </select>
            </div>
          </div>

          {loading ? (
            <div className={styles.sectionLoading}>{t('common.loading')}</div>
          ) : (
            <>
              <div className={styles.tableContainer}>
                <table className={styles.usersTable}>
                  <thead>
                    <tr>
                      <th>{t('admin.users.email')}</th>
                      <th>{t('admin.users.name')}</th>
                      <th>{t('admin.users.role')}</th>
                      <th>{t('admin.users.created')}</th>
                      <th>{t('admin.tokens.jobPostTokens', { defaultValue: 'JobPost Tokens' })}</th>
                      <th>{t('admin.tokens.quickSearchTokens', { defaultValue: 'QuickSearch Tokens' })}</th>
                      <th>{t('admin.tokens.giftActions', { defaultValue: 'Gift Tokens' })}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((user) => (
                      <tr key={user._id}>
                        <td>{user.email}</td>
                        <td>{user.fullName}</td>
                        <td>
                          <span className={getRoleBadgeClass(user.role.toLowerCase())}>{user.role}</span>
                        </td>
                        <td>{new Date(user.createdAt).toLocaleDateString(t('common.locale') || 'en-US')}</td>
                        <td>{getEmployerTokenCount(user, 'jobPost')}</td>
                        <td>{getEmployerTokenCount(user, 'quickSearch')}</td>
                        <td>
                          <div className={styles.giftControls}>
                            <button
                              className={styles.giftButton}
                              onClick={() => openGiftModal(user, 'jobPost')}
                              disabled={giftLoading[user._id]?.jobPost}
                            >
                              {giftLoading[user._id]?.jobPost
                                ? t('common.loading')
                                : t('admin.tokens.giftJobPost', { defaultValue: 'Gift JobPost' })}
                            </button>
                            <button
                              className={styles.giftButton}
                              onClick={() => openGiftModal(user, 'quickSearch')}
                              disabled={giftLoading[user._id]?.quickSearch}
                            >
                              {giftLoading[user._id]?.quickSearch
                                ? t('common.loading')
                                : t('admin.tokens.giftQuickSearch', { defaultValue: 'Gift QuickSearch' })}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {users.length === 0 && (
                  <div className={styles.noResults}>{t('admin.users.noResults')}</div>
                )}
              </div>

              {totalPages > 1 && (
                <div className={styles.pagination}>
                  <button
                    className={styles.paginationBtn}
                    onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                    disabled={currentPage === 1}
                  >
                    ← {t('common.previous')}
                  </button>
                  <span className={styles.paginationInfo}>
                    {t('common.pageOf', { current: currentPage, total: totalPages })}
                  </span>
                  <button
                    className={styles.paginationBtn}
                    onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                    disabled={currentPage === totalPages}
                  >
                    {t('common.next')} →
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {giftModal.isOpen && giftModal.user && (
        <div className={adminCommonStyles.modalOverlay} onClick={closeGiftModal}>
          <div className={adminCommonStyles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div className={adminCommonStyles.modalHeader}>
              <h2>{getTokenTypeLabel(giftModal.type)}</h2>
              <button className={adminCommonStyles.modalClose} onClick={closeGiftModal} aria-label={t('common.close')}>
                ✕
              </button>
            </div>
            <div className={`${adminCommonStyles.modalBody} ${styles.tokenModalBody}`}>
              <p className={styles.tokenTargetText}>
                {giftModal.user.fullName} ({giftModal.user.email})
              </p>

              <div className={styles.tokenPresetGrid}>
                {(['1', '3', '5', 'custom'] as AmountChoice[]).map((choice) => (
                  <button
                    key={choice}
                    className={`${styles.tokenPresetButton} ${giftModal.amountChoice === choice ? styles.tokenPresetButtonActive : ''}`}
                    onClick={() => setGiftModal(prev => ({ ...prev, amountChoice: choice }))}
                  >
                    {choice === 'custom'
                      ? t('admin.tokens.custom', { defaultValue: 'Custom' })
                      : choice}
                  </button>
                ))}
              </div>

              {giftModal.amountChoice === 'custom' && (
                <div className={styles.tokenCustomRow}>
                  <input
                    type="number"
                    min="1"
                    step="1"
                    value={giftModal.customAmount}
                    onChange={(e) => setGiftModal(prev => ({ ...prev, customAmount: e.target.value }))}
                    placeholder={t('admin.tokens.amount', { defaultValue: 'Amount' })}
                    className={styles.tokenCustomInput}
                  />
                </div>
              )}

              <p className={styles.tokenConfirmText}>
                {t('admin.tokens.confirmPreview', {
                  defaultValue: 'You are about to gift {{amount}} {{tokenType}} token(s) to {{email}}.',
                  amount: getResolvedGiftAmountLabel(),
                  tokenType: giftModal.type === 'jobPost' ? 'JobPost' : 'QuickSearch',
                  email: giftModal.user.email,
                })}
              </p>
            </div>
            <div className={adminCommonStyles.modalFooter}>
              <button className={adminCommonStyles.btnCancel} onClick={closeGiftModal}>
                {t('common.cancel')}
              </button>
              <button
                className={styles.tokenConfirmButton}
                onClick={confirmGiftFromModal}
                disabled={giftLoading[giftModal.user._id]?.[giftModal.type]}
              >
                {giftLoading[giftModal.user._id]?.[giftModal.type]
                  ? t('common.loading')
                  : t('common.confirm')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminTokens;
